import {
  mergeGuidedIntoDestination,
  mergeGuidedOnCurrentPath,
} from "./trainingPlansV2GuidedTaskParams";

/**
 * Registry of hands-on guided flows for Training Plans V2.
 *
 * Each step has `navigate`:
 * - `{ path, search? }` — go to this screen for the step (full pathname under /home/…)
 * - `'stay'` — keep the current route; only the guided step index (`gs`) updates (you work here).
 *
 * Each flow gets `helpArticleSlug`: **`inline-training-plans-{id}`** (same as the flow’s stable `id`).
 * Create matching articles in m45-admin (category **inline**, coach **user_type**) for markdown + audio on **?**.
 * Bulk seed (all flows except **create-reusable-plan**): `node admin/scripts/seed-inline-common-task-articles.js`.
 *
 * To add a flow: append to the raw list below. Keep `id` stable (URLs + analytics + slug).
 */

const LIB = "/home/training-plans-v2/library";
const NEW = "/home/training-plans-v2/new";

/** Default Firestore slug for inline articles: `inline-training-plans-{flowId}`. */
export function inlineHelpArticleSlugForFlowId(flowId) {
  return `inline-training-plans-${flowId}`;
}

const TRAINING_PLANS_V2_COMMON_TASK_FLOWS_RAW = [
  {
    id: "create-reusable-plan",
    title: "Create a new reusable plan",
    summary:
      "We go to the library, open a new reusable plan, then build and save in the editor together.",
    icon: "PlaylistAddCheck",
    steps: [
      {
        title: "Library — reusable plans",
        body:
          "You’re in the **Library** (Reusable Plans). When you’re ready, use **Create Reusable Plan** in the toolbar above, or the shortcut below.",
        navigate: { path: LIB, search: {} },
        shortcut: { label: "Create Reusable Plan", path: NEW, search: { type: "template" } },
      },
      {
        title: "Name and describe the plan",
        body:
          "In the editor, set a clear **plan name** and **description** so you and your team know what this version is for. **Next step** only moves the guide—you can use it before or after **Save changes**; it will not clear what you typed.",
        navigate: { path: NEW, search: { type: "template" } },
      },
      {
        title: "Build workouts",
        body:
          "Add workouts and exercises. Use **Save changes** as you go so your work is kept—if you leave this page without saving, you can lose edits. It’s fine to stay in **draft** until you’re ready to assign or publish.",
        navigate: "stay",
      },
      {
        title: "Assign or publish",
        body:
          "When it’s ready, use the toolbar **Assign** or **Publish** (and **Manage** for versions). Exit this guide when you’re done.",
        navigate: "stay",
      },
    ],
  },
  {
    id: "create-draft-version",
    title: "Create a draft of a plan",
    summary:
      "We start in the library, branch a **New Draft**, then edit and save without disturbing live assignments.",
    icon: "Difference",
    steps: [
      {
        title: "Open the library",
        body:
          "Find the plan you want to evolve. Use **Work In Progress** or **All Plans** if the list is long.",
        navigate: { path: LIB, search: {} },
      },
      {
        title: "Start a new draft",
        body:
          "In the plan row **⋯** menu, choose **New Draft**. That creates a new version in the same family.",
        navigate: "stay",
      },
      {
        title: "Edit the draft",
        body:
          "You should be in the editor on the new draft. **Save changes** as you go.",
        navigate: "stay",
      },
      {
        title: "Optional: checkpoint",
        body:
          "For a durable checkpoint without publishing, use the editor **⋯** menu → **Save as new draft version** when you need it.",
        navigate: "stay",
      },
    ],
  },
  {
    id: "assign-plan-to-clients",
    title: "Assign a plan to clients",
    summary:
      "We use the library to pick a plan, then you open **Assign** and confirm clients on the assign screen.",
    icon: "GroupAdd",
    steps: [
      {
        title: "Pick a plan",
        body:
          "In the **Library**, find the plan to assign. Archived plans can’t be assigned—pick an active version.",
        navigate: { path: LIB, search: {} },
      },
      {
        title: "Open Assign",
        body:
          "Use the row **Assign** action (or open the plan and click **Assign** in the toolbar). Stay on this screen for the next step.",
        navigate: "stay",
      },
      {
        title: "Select clients and confirm",
        body:
          "Choose clients, read any warnings, and confirm. You’re done when assignments match what you intended.",
        navigate: "stay",
      },
      {
        title: "Staging note",
        body:
          "If the library shows **Staged** for an active plan, finish **Publish** or **Revert** in the editor so clients see what you expect.",
        navigate: "stay",
      },
    ],
  },
  {
    id: "assign-client-plan-variant",
    title: "Assign a client plan to another client (variant)",
    summary:
      "From the source client’s plan, open **Assign**, pick the other client, and create a **variant** — as their active plan or as a draft only.",
    icon: "AltRoute",
    steps: [
      {
        title: "Library — source client",
        body:
          "Scope → **Client Plans**, then choose the client **whose plan you are copying from**. Find that plan in the list.",
        navigate: { path: LIB, search: { scope: "client" } },
      },
      {
        title: "Open Assign",
        body:
          "Use the row **Assign** action (or open the plan and click **Assign** in the toolbar). This stays tied to the source plan.",
        navigate: "stay",
      },
      {
        title: "Select Target — destination client",
        body:
          "Add the **other** client — the one who should receive a copy. Cross-client assignment uses a variant copy so each client keeps their own plan document.",
        navigate: "stay",
      },
      {
        title: "Assignment Mode",
        body:
          "Choose **Create Special Variant and Assign** to set their **primary** to the new copy, or **Create Variant as Draft** to add a draft only (their current primary stays until you publish and switch later).",
        navigate: "stay",
      },
      {
        title: "Review and confirm",
        body:
          "Read any warnings, then confirm. For **draft** variants, you may land in **Edit** on the new copy — **Save changes**, then **Publish** when ready.",
        navigate: "stay",
      },
    ],
  },
  {
    id: "alternatives-and-remove",
    title: "Alternative plans and removing alternatives",
    summary:
      "Add a second plan alongside a client’s primary, or remove an alternative when they no longer need it.",
    icon: "SwapHoriz",
    steps: [
      {
        title: "Library — pick the plan",
        body:
          "In the **Library**, find the plan version you want as an **alternative** for someone. It must be assignable (not archived).",
        navigate: { path: LIB, search: {} },
      },
      {
        title: "Open Assign",
        body:
          "Use the row **Assign** button, or open the plan and choose **Assign** in the toolbar.",
        navigate: "stay",
      },
      {
        title: "Select the client",
        body:
          "In **Select Target**, choose the client. This screen shows who already has this plan and how.",
        navigate: "stay",
      },
      {
        title: "Assignment mode — alternative",
        body:
          "Under **Assignment Mode**, pick the option that adds this plan as an **alternative** (a second plan alongside their **primary**). Do not choose **Set as Active** unless you mean to change their primary workout plan.",
        navigate: "stay",
      },
      {
        title: "Confirm",
        body:
          "Read warnings, then confirm. The client can have a primary and one or more alternatives depending on your setup.",
        navigate: "stay",
      },
      {
        title: "Remove an alternative",
        body:
          "To remove an alternative: open **Assign** for that plan → **Who has this plan** → find the client → **Remove alternative**. This only removes the link for that client; it does not delete the plan document.",
        navigate: "stay",
      },
      {
        title: "Double-check",
        body:
          "Return to the **Library** if needed and verify assignments match what you intended.",
        navigate: "stay",
      },
    ],
  },
  {
    id: "publish-draft",
    title: "Publish a draft for clients",
    summary:
      "We find your draft, finish edits, then publish so the live version updates for assigned clients.",
    icon: "Publish",
    steps: [
      {
        title: "Find drafts",
        body:
          "In the **Library**, use **Work In Progress** (or filters) to find the draft you want to publish.",
        navigate: { path: LIB, search: { status: "in_progress" } },
      },
      {
        title: "Edit and save",
        body:
          "Open the draft in **Edit**. Use **Save changes**. If the toolbar shows **Staging** on a plan that is **active** and already **assigned** to clients, that means older edits are still waiting to go live—**Publish** those edits first, or **Revert changes** to discard them, before you publish this draft version.",
        navigate: "stay",
      },
      {
        title: "Publish",
        body:
          "Click **Publish** and confirm. Afterward, spot-check **Assign** or **Manage** if needed.",
        navigate: "stay",
      },
      {
        title: "Tell your clients (optional)",
        body:
          "For big changes, message clients so they know what to expect in the app this week.",
        navigate: "stay",
      },
    ],
  },
  {
    id: "duplicate-or-branch",
    title: "Duplicate a plan",
    summary:
      "We duplicate from the library, then you rename, edit, and assign the copy.",
    icon: "ContentCopy",
    steps: [
      {
        title: "Library",
        body:
          "Locate the plan you want to copy (reusable or client plan).",
        navigate: { path: LIB, search: {} },
      },
      {
        title: "Duplicate",
        body:
          "Row **⋯** → **Duplicate**. Give the copy a clear name (client, phase, or date).",
        navigate: "stay",
      },
      {
        title: "Edit and assign",
        body:
          "Open the copy in **Edit**, adjust workouts, then **Assign** or keep it as a template.",
        navigate: "stay",
      },
    ],
  },
  {
    id: "make-reusable-from-client-plan",
    title: "Make a reusable plan from a client plan",
    summary:
      "We duplicate workouts into a new **Reusable Plan** (org-wide template), then you open it under **Reusable Plans** to name, save, and assign.",
    icon: "Transform",
    steps: [
      {
        title: "Library — client plan",
        body:
          "Scope → **Client Plans** and choose the client. Find the plan whose structure you want as a reusable template.",
        navigate: { path: LIB, search: { scope: "client" } },
      },
      {
        title: "Duplicate as reusable plan",
        body:
          "Row **⋯** → **Duplicate as reusable plan**. That creates a **new** reusable copy — the client’s plan stays as-is. (Alternatively: open **Edit** → **Settings** → **Plan Type** → **Reusable Plan** to promote **this** document instead of copying.)",
        navigate: "stay",
      },
      {
        title: "Open it under Reusable Plans",
        body:
          "The new plan **does not** stay in this client list — switch **Scope** to **Reusable Plans**. The success message may include a **Reusable Plans** shortcut. Open the copy to rename it if needed.",
        navigate: { path: LIB, search: { scope: "template" } },
      },
      {
        title: "Save, publish, assign",
        body:
          "In **Edit**, use **Save changes**. When you’re ready, **Publish** and **Assign** (or keep it as a draft until then).",
        navigate: "stay",
      },
      {
        title: "Optional: retire the old client copy",
        body:
          "If the client-only draft is no longer needed, you can archive or clean it up from **Client Plans** — your new reusable lives separately.",
        navigate: "stay",
      },
    ],
  },
  {
    id: "import-plan-from-file",
    title: "Import a plan from a file",
    summary:
      "We use **Import plan** on the library, then clean up the result in the editor.",
    icon: "UploadFile",
    steps: [
      {
        title: "Prepare a file",
        body:
          "Use a structured PDF or image (weeks/days/exercises import best).",
        navigate: { path: LIB, search: {} },
      },
      {
        title: "Import",
        body:
          "On **Library**, use **Import plan**, pick **Import format** if asked, and wait for processing.",
        navigate: "stay",
      },
      {
        title: "Review in the editor",
        body:
          "Treat the result as a **starting point**: fix exercises, sets/reps, and names. **Save changes** often.",
        navigate: "stay",
      },
      {
        title: "Finish",
        body:
          "When it matches your standard, publish or assign. Save a reusable copy if you’ll reuse the structure.",
        navigate: "stay",
      },
    ],
  },
];

export const TRAINING_PLANS_V2_COMMON_TASK_FLOWS = TRAINING_PLANS_V2_COMMON_TASK_FLOWS_RAW.map((flow) => ({
  ...flow,
  helpArticleSlug: inlineHelpArticleSlugForFlowId(flow.id),
}));

export function getCommonTaskFlowById(id) {
  return TRAINING_PLANS_V2_COMMON_TASK_FLOWS.find((f) => f.id === id) || null;
}

/**
 * Full app URL for a guided step (includes `gt` / `gs`).
 * @param {{ id: string, steps: Array<{ navigate: object | 'stay' }> }} flow
 */
export function getGuidedStepDestination(flow, stepIndex, location, fromSearch) {
  const step = flow.steps[stepIndex];
  if (!step) return null;
  const guided = { flowId: flow.id, stepIndex };
  if (step.navigate === "stay") {
    return mergeGuidedOnCurrentPath(location.pathname, location.search, guided);
  }
  const { path, search = {} } = step.navigate;
  const sp = new URLSearchParams();
  Object.entries(search).forEach(([k, v]) => {
    if (v != null && v !== "") sp.set(k, String(v));
  });
  return mergeGuidedIntoDestination(path, sp.toString(), fromSearch, guided);
}
